import axios from "axios";
import OperationTypes from "../../../types";
import { IVehicleFirstFueling, TransferRequestForExternalDTTResponse } from "../types";
import { ServerSideTypes } from "../../../types/OperationCommonTypes";

export enum EnumStatusReasons {
  AVAILABLE = "AVAILABLE",
  NOT_AVAILABLE = "NOT_AVAILABLE",
}

interface IStatusReason {
  "id": 7;
  "code": "DDT";
  "descr": "Transport Document";
  "status": "NOT_AVAILABLE";
  "valid": true;
  "orderNumber": 10;
  "reserved": true;
  "ddt": true;
  "transferable": false;
  "damages": false;
  "default": true;
}

const VehicleEntranceService = {
  get: {
    allDrivers: (tenant: string) => {
      return axios
        .get(`/masterdatatransportation/driver/all`, {
          params: { tenant: tenant },
        })
        .then(({ data }) => {
          return data.map((driver: any) => {
            return {
              ...driver,
              label: `${driver.name} ${driver.surname}`,
              value: driver.sapDriverId,
            };
          });
        });
    },

    allLocations: (tenant: string) => {
      return axios
        .get(`/masterdatavehicle/location/all`, { params: { tenant: tenant } })
        .then(({ data }) => {
          return data.map((location: any) => {
            return {
              ...location,
              label: location.description,
              value: location.description,
            };
          });
        });
    },

    checkList: (tenant: string, enginePowerCode: string | null) => {
      return axios
        .get(`/masterdatasupplier/checkList/all`, {
          params: {
            tenant: tenant,
            ...(enginePowerCode && { enginePowerCode: enginePowerCode }),
          },
        })
        .then(({ data }) => {
          return data.map((check: any) => {
            return {
              ...check,
            };
          });
        });
    },

    vehicleByDDT: (tenant: string, plate: string) => {
      const url = encodeURIComponent(plate);
      return axios
        .get<OperationTypes.Vehicle>(
          `/masterdatatransportation/transferrequest/deliveryNote?deliveryNoteNumber=${url}`,
          { params: { tenant: tenant } }
        )
        .then((response) => response.data);
    },

    downloadDeliveryNote: (tenant: string, id: number | null) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `/masterdatatransportation/deliveryNote/${id}/file`,
          {
            params: { tenant: tenant },
            responseType: "blob",
          }
        )
        .then((response) => response);
    },

    vehicleByPlate: (tenant: string, plate: string) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `masterdatavehicle/vehicle/plate/${plate}`,
          { params: { tenant: tenant } }
        )
        .then((response) => response.data);
    },

    garagesLocations: (tenant: string) => {
      return axios
        .get(`/masterdatasupplier/location/locationsByFilters`, {
          params: {
            tenant: tenant,
            pageNumber: 0,
            pageSize: 1000000,
          },
        })
        .then(({ data }) => {
          return data.content.map((location: any) => {
            return {
              ...location,
              label: location.locationEntityDesc,
              value: location.locationEntityDesc,
            };
          });
        });
    },

    vehicleByPlateFirstCheckin: (tenant: string, plate: string) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `/masterdatatransportation/operatorapp/plate/${plate}/status`,
          {
            params: { tenant: tenant },
          }
        )
        .then((response) => response.data);
    },

    platesByReferenceLocationIdWithDdt: (tenant: string, locationId: string, referenceLocationType: string) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `/masterdatavehicle/vehicle/referenceLocation/${locationId}/${referenceLocationType}/ddt`,
          {
            params: { tenant: tenant },
          }
        )
        .then(({ data }: any) => {
          return data.map((vehicle: any) => {
            return {
              ...vehicle,
              label: vehicle.plate,
              value: vehicle.plate,
            };
          });
        });
    },

    repariShopPlatesByReferenceLocationIdWithDdt: (
      tenant: string,
      locationId: string
    ) => {
      return axios
        .get<OperationTypes.Vehicle>(
          `/masterdatavehicle/vehicle/shopRepair/comingFrom/${locationId}/ddt`,
          {
            params: { tenant: tenant },
          }
        )
        .then(({ data }: any) => {
          return data.map((vehicle: any) => {
            return {
              ...vehicle,
              label: vehicle.plate,
              value: vehicle.plate,
            };
          });
        });
    },
    missingEquipments: (tenant: string, plate: string) => {
      return axios.get<ServerSideTypes.IResult<OperationTypes.Equipmet>>("/masterdatadamage/equipments/byfilters", {
        params: {
          tenant,
          plate,
          present: false,
          pageNumber: 0,
          pageSize: 1000
        }
      }).then(
        (response) => response.data
      )
    },
    statusReasonsDDT: (tenant: string) => {
      return axios.get<IStatusReason[]>("/masterdatavehicle/statusreason/all", {
        params: {
          tenant,
          includeSpecialCodes: true,
          ddt: true,
        }
      }).then(
        response => response.data,
      )
    },
    openTransferRequestsByPlatesInTFWORK: (tenant: string, plates: string[]) => {
      return axios.get<TransferRequestForExternalDTTResponse[]>("/masterdatatransportation/operatorapp/getOpenTransferRequestsByPlatesInTFWORK", {
        params: {
          tenant,
          plates: plates.join(",")
        }
      }).then(
        response => response.data,
      )
    }
  },

  add: {
    confirmEntrance: (tenant: string, data: any) => {
      return axios
        .post(
          "masterdatatransportation/operatorapp/externalDeliveryNote",
          data,
          { params: { tenant: tenant } }
        )
        .then((response) => response.data);
    },
    confirmEntranceInternal: (tenant: string, data: any) => {
      return axios
        .post("masterdatatransportation/operatorapp/checkin", data, {
          params: { tenant: tenant },
        })
        .then((response) => response.data);
    },

    vehicleFirstCheckin: (tenant: string, data: any) => {
      return axios
        .post<OperationTypes.Vehicle>(
          `/masterdatatransportation/operatorapp/firstCheckIn`,
          data,
          {
            params: { tenant: tenant },
          }
        )
        .then((response) => response.data);
    },
  },

  firstFueling: {
    get: {
      vehicleByPlate: (tenant: string, plate: string) => {
        return axios
          .get<OperationTypes.Vehicle>(
            `masterdatavehicle/vehicle/plate/${plate}`,
            {
              params: { tenant: tenant },
            }
          )
          .then((response) => response.data);
      },
      validateFirstFuelingByPlate: (tenant: string, plate: string) => {
        return axios
          .get(
            `masterdatavehicle/firstfueling/${plate}/validateFirstFuelingByPlate`,
            { params: { tenant } }
          )
          .then((response) => {
            return response.data;
          });
      },
      checkByPlate: (tenant: string, plate: string) => {
        return axios
          .get<boolean>(
            `/masterdatavehicle/firstfueling/${plate}/hasDoneFirstFuelingByPlate`,
            {
              params: { tenant: tenant, plate: plate },
            }
          )
          .then((response) => {
            return response.data;
          });
      },

      checkById: (tenant: string, id: number) => {
        return axios
          .get<boolean>(
            `/masterdatavehicle/firstfueling/${id}/hasDoneFirstFuelingById`,
            {
              params: { tenant: tenant, id: id },
            }
          )
          .then((response) => {
            return response.data;
          });
      },

      vehicleById: (tenant: string, id: number) => {
        return axios
          .get<IVehicleFirstFueling>(
            `/masterdatavehicle/firstfueling/${id}/getVehicleFirstFuelingById`,
            {
              params: { tenant: tenant, id: id },
            }
          )
          .then((response) => {
            return response.data;
          });
      },

      firstFuelReceipt: (tenant: string, id: number) => {
        return axios
          .get(`/masterdatavehicle/firstfueling/${id}/c`, {
            params: { tenant: tenant, id: id },
          })
          .then((response) => {
            return response;
          });
      },
      availabilityStatusReasons: (
        tenant: string,
        statusReason: EnumStatusReasons,
        includeSpecialCodes: boolean
      ) => {
        return axios
          .get<OperationTypes.Domain[]>("masterdatavehicle/statusreason/all", {
            params: {
              tenant: tenant,
              valid: true,
              status: statusReason,
              includeSpecialCodes,
            },
          })
          .then((response) => response.data);
      },
      unavailabilityByVehicle: (tenant: string, id: number) => {
        return axios
          .get<OperationTypes.VehicleAvailability.Status>(
            `masterdatavehicle/unavailability/unavailabilityByVehicle/${id}`,
            { params: { tenant: tenant } }
          )
          .then((response) => response.data);
      },
    },
    post: {
      doFirstFueling: (tenant: string, data: FormData) => {
        return axios.post(
          "masterdatavehicle/firstfueling/doFirstFueling",
          data,
          {
            params: { tenant },
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      },
      changeAvailabilityReason: (
        tenant: string,
        vehicleId: number,
        availabilityCode: string
      ) => {
        const data = { vehicleId, availabilityCode };
        return axios
          .post<OperationTypes.VehicleAvailability.Status>(
            "masterdatavehicle/unavailability/changeAvailabilityReason",
            data,
            { params: { tenant: tenant } }
          )
          .then((response) => response.data);
      },
    },
  },
};

export default VehicleEntranceService;
